import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  FileText, 
  Upload, 
  Download, 
  Trash2, 
  Loader2,
  File,
  Image
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Documento {
  id: string;
  nome_arquivo: string;
  nome_original: string;
  tipo_documento: string;
  tipo_arquivo: string;
  tamanho_bytes: number;
  descricao?: string;
  created_at: string;
}

interface VendedorDocumentosTabProps {
  vendedorId: string;
}

const TIPOS_DOCUMENTO = [
  { value: 'RG', label: 'RG' },
  { value: 'CPF', label: 'CPF' },
  { value: 'CNH', label: 'CNH' },
  { value: 'Comprovante de Residência', label: 'Comprovante de Residência' },
  { value: 'Diploma', label: 'Diploma' },
  { value: 'Certificado', label: 'Certificado' },
  { value: 'Contrato', label: 'Contrato' },
  { value: 'Outros', label: 'Outros' },
];

export function VendedorDocumentosTab({ vendedorId }: VendedorDocumentosTabProps) {
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentoToDelete, setDocumentoToDelete] = useState<Documento | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [tipoDocumento, setTipoDocumento] = useState('Outros');
  const [descricao, setDescricao] = useState('');
  const { toast } = useToast();

  const fetchDocumentos = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Usuário não autenticado');
      }

      const response = await fetch(`/api/vendedor-documentos/${vendedorId}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar documentos');
      }

      const data = await response.json();
      setDocumentos(data.documentos || []);
    } catch (error) {
      console.error('Erro ao buscar documentos:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os documentos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (vendedorId) {
      fetchDocumentos();
    }
  }, [vendedorId]);

  const handleUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      toast({
        title: 'Atenção',
        description: 'Selecione pelo menos um arquivo',
        variant: 'destructive',
      });
      return;
    }

    try {
      setUploading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Usuário não autenticado');
      }

      const formData = new FormData();
      Array.from(selectedFiles).forEach(file => {
        formData.append('documentos', file);
      });
      formData.append('vendedorId', vendedorId);
      formData.append('tipoDocumento', tipoDocumento);
      if (descricao) {
        formData.append('descricao', descricao);
      }

      const response = await fetch('/api/vendedor-documentos/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao fazer upload');
      }

      toast({
        title: 'Sucesso',
        description: 'Documento(s) enviado(s) com sucesso',
      });

      // Limpar formulário
      setSelectedFiles(null);
      setTipoDocumento('Outros');
      setDescricao('');
      setUploadDialogOpen(false);
      
      // Atualizar lista
      fetchDocumentos();
    } catch (error) {
      console.error('Erro no upload:', error);
      toast({
        title: 'Erro',
        description: error instanceof Error ? error.message : 'Erro ao fazer upload',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (documento: Documento) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Usuário não autenticado');
      }

      const response = await fetch(`/api/vendedor-documentos/download/${documento.id}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Erro ao gerar link de download');
      }

      const data = await response.json();
      
      // Abrir URL em nova aba
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível fazer o download do documento',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!documentoToDelete) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Usuário não autenticado');
      }

      const response = await fetch(`/api/vendedor-documentos/${documentoToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Erro ao deletar documento');
      }

      toast({
        title: 'Sucesso',
        description: 'Documento deletado com sucesso',
      });

      setDeleteDialogOpen(false);
      setDocumentoToDelete(null);
      fetchDocumentos();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível deletar o documento',
        variant: 'destructive',
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFileIcon = (tipoArquivo: string) => {
    if (tipoArquivo.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
    if (tipoArquivo.includes('image')) return <Image className="h-5 w-5 text-blue-500" />;
    return <File className="h-5 w-5 text-gray-500" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header com botão de upload */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Documentos Pessoais</h3>
          <p className="text-sm text-muted-foreground">
            Gerencie os documentos pessoais do vendedor
          </p>
        </div>
        <Button onClick={() => setUploadDialogOpen(true)}>
          <Upload className="h-4 w-4 mr-2" />
          Enviar Documento
        </Button>
      </div>

      {/* Lista de documentos */}
      {documentos.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhum documento encontrado</h3>
            <p className="text-muted-foreground text-center max-w-md mb-4">
              Este vendedor ainda não possui documentos cadastrados. Clique no botão acima para enviar documentos.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {documentos.map((documento) => (
            <Card key={documento.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {getFileIcon(documento.tipo_arquivo)}
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-sm whitespace-normal break-words">
                        {documento.nome_original}
                      </CardTitle>
                      <CardDescription className="text-xs">
                        {documento.tipo_documento}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>Tamanho: {formatFileSize(documento.tamanho_bytes)}</p>
                  <p>Enviado em: {formatDate(documento.created_at)}</p>
                  {documento.descricao && (
                    <p className="italic">"{documento.descricao}"</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(documento)}
                    className="flex-1"
                  >
                    <Download className="h-3 w-3 mr-1" />
                    Baixar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setDocumentoToDelete(documento);
                      setDeleteDialogOpen(true);
                    }}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog de Upload */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Enviar Documento</DialogTitle>
            <DialogDescription>
              Selecione um ou mais arquivos para enviar
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="tipo">Tipo de Documento</Label>
              <Select value={tipoDocumento} onValueChange={setTipoDocumento}>
                <SelectTrigger id="tipo">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_DOCUMENTO.map((tipo) => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição (opcional)</Label>
              <Input
                id="descricao"
                placeholder="Ex: RG frente e verso"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="arquivos">Arquivos</Label>
              {/* Input nativo oculto para evitar overflow de nomes extensos */}
              <Input
                id="arquivos"
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => setSelectedFiles(e.target.files)}
                className="hidden"
              />
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="arquivos"
                  className="cursor-pointer bg-primary/10 hover:bg-primary/20 text-primary font-medium py-2 px-4 rounded-md inline-block transition-colors"
                >
                  Escolher Arquivos
                </Label>
                {/* Resumo do que foi selecionado (truncado) */}
                <div className="text-sm text-muted-foreground min-w-0 flex-1">
                  {selectedFiles && selectedFiles.length > 0 ? (
                    <span className="block whitespace-normal break-words">
                      {selectedFiles.length === 1
                        ? selectedFiles[0].name
                        : `${selectedFiles.length} arquivos selecionados`}
                    </span>
                  ) : (
                    <span className="block whitespace-normal break-words">Nenhum arquivo selecionado</span>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Formatos aceitos: PDF, JPG, PNG, DOC, DOCX (máx. 5MB por arquivo)
              </p>
            </div>
            {selectedFiles && selectedFiles.length > 0 && (
              <div className="bg-muted p-3 rounded-md min-w-0">
                <p className="text-sm font-medium mb-2">
                  {selectedFiles.length} arquivo(s) selecionado(s):
                </p>
                <ul className="text-xs space-y-1">
                  {Array.from(selectedFiles).map((file, index) => (
                    <li key={index} className="whitespace-normal break-words">• {file.name} ({formatFileSize(file.size)})</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setUploadDialogOpen(false);
                setSelectedFiles(null);
                setDescricao('');
              }}
              disabled={uploading}
            >
              Cancelar
            </Button>
            <Button onClick={handleUpload} disabled={uploading || !selectedFiles}>
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Enviar
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o documento "{documentoToDelete?.nome_original}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDocumentoToDelete(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

